'use client';

import React, { useState } from 'react';
import { ExperienceType } from '../types';
import { colors } from '@/design-system/tokens/colors';
import { typography } from '@/design-system/tokens/typography';
import { spacing } from '@/design-system/tokens/spacing';
import Border from '../../common/border';

interface ExperienceFAQProps {
  experience: unknown;
  type: ExperienceType;
}

interface FAQItem {
  question?: string;
  answer?: string;
}

export const ExperienceFAQ: React.FC<ExperienceFAQProps> = ({ experience }) => {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  if (!experience) return null;

  const getFAQ = (): FAQItem[] => {
    const exp = experience as {
      faq?: FAQItem[];
      guidelines?: { faqs?: FAQItem[] };
    };
    return exp?.faq || exp?.guidelines?.faqs || [];
  };

  const faq = getFAQ();

  if (!Array.isArray(faq) || faq.length === 0) return null;

  const toggleFAQ = (index: number) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: spacing[6] }}>
      <h2
        style={{
          fontSize: typography.size.lg,
          fontWeight: typography.weight.semibold,
          color: colors.typography.primary,
          fontFamily: typography.fonts.primary,
          lineHeight: typography.lineHeight.none,
        }}
      >
        Frequently Asked Questions
      </h2>
      <div
        style={{ display: 'flex', flexDirection: 'column', gap: spacing[3] }}
      >
        {faq.map((item, index) => {
          const isExpanded = expandedIndex === index;
          return (
            <div
              key={index}
              style={{
                backgroundColor: '#fbfbfb', // Very light grey - not in design system but matches Figma
                border: `1px solid ${colors.stroke.lightest}`,
                borderRadius: '12px',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: spacing[2],
                  padding: isExpanded
                    ? spacing[4]
                    : `${spacing[2]} ${spacing[4]}`,
                }}
              >
                <button
                  onClick={() => toggleFAQ(index)}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    textAlign: 'left',
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                  }}
                >
                  <span
                    style={{
                      fontFamily: typography.fonts.primary,
                      fontSize: typography.size.base,
                      fontWeight: typography.weight.regular,
                      color: colors.gray[800], // #262626 is close to gray-800 (#292524)
                      flex: 1,
                    }}
                  >
                    {item.question}
                  </span>
                  <div
                    style={{
                      backgroundColor: colors.background.primary,
                      display: 'flex',
                      alignItems: 'center',
                      padding: spacing[2],
                      borderRadius: '8px',
                      flexShrink: 0,
                    }}
                  >
                    <svg
                      width='20'
                      height='20'
                      viewBox='0 0 20 20'
                      fill='none'
                      style={{
                        transform: isExpanded
                          ? 'rotate(180deg)'
                          : 'rotate(0deg)',
                        transition: 'transform 0.2s ease',
                      }}
                    >
                      <path
                        d='M5 7.5l5 5 5-5'
                        stroke='currentColor'
                        strokeWidth='2'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      />
                    </svg>
                  </div>
                </button>
                {isExpanded && item.answer && (
                  <p
                    style={{
                      fontFamily: typography.fonts.primary,
                      fontSize: typography.size.sm,
                      fontWeight: typography.weight.regular,
                      color: colors.gray[700], // #4c4c4d is close to gray-700 (#44403C)
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {item.answer}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <div style={{ paddingTop: spacing[4] }}>
        <Border />
      </div>
    </div>
  );
};
